// frontend/src/components/crm/CampaignAnalytics.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { campaignsAPI } from '../../utils/crmApi';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { StatusBadge } from '../ui/status-components';
import { useBranding } from '../../contexts/BrandingContext';
import {
    ArrowLeftIcon,
    ChartBarIcon,
    EnvelopeIcon,
    CheckCircleIcon,
    XCircleIcon,
    EyeIcon,
    CursorArrowRaysIcon,
    ArrowPathIcon,
    ExclamationTriangleIcon,
    LinkIcon,
    UserGroupIcon,
    ClockIcon
} from '@heroicons/react/24/outline';
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import toast from 'react-hot-toast';

const CampaignAnalytics = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { branding } = useBranding();
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [campaign, setCampaign] = useState(null);
    const [timeframe, setTimeframe] = useState('7d'); // 7d, 30d, 90d, all

    useEffect(() => {
        loadAnalytics();
    }, [id, timeframe]);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            const [analyticsRes, campaignRes] = await Promise.all([
                campaignsAPI.getAnalytics(id, { granularity: 'day' }),
                campaignsAPI.getById(id)
            ]);

            if (analyticsRes.data.success) {
                setAnalytics(analyticsRes.data.data);
            }
            if (campaignRes.data.success) {
                setCampaign(campaignRes.data.data);
            }
        } catch (error) {
            console.error('Failed to load analytics:', error);
            toast.error('Failed to load campaign analytics');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="text-center py-12">Loading analytics...</div>
            </div>
        );
    }

    if (!analytics || !campaign) {
        return (
            <div className="p-6">
                <div className="text-center py-12 text-muted-foreground">
                    Campaign not found or analytics unavailable
                </div>
            </div>
        );
    }

    const overview = analytics.overview || {};
    const stepAnalytics = analytics.stepAnalytics || {};

    // Generate time-series data from recipients (if available)
    // In production, this should come from backend with proper time-series aggregation
    const generateTimeSeriesData = () => {
        // If no data, return empty array
        if (!overview.sent || overview.sent === 0) {
            return [];
        }

        // Determine the start date - use the earliest of: sendAt, startedAt, or createdAt
        // Don't show dates before the campaign existed
        const now = new Date();
        let startDate = new Date(campaign.createdAt || now);
        
        if (campaign.sendAt) {
            const sendDate = new Date(campaign.sendAt);
            if (sendDate < startDate) startDate = sendDate;
        }
        
        if (campaign.startedAt) {
            const startedDate = new Date(campaign.startedAt);
            if (startedDate < startDate) startDate = startedDate;
        }
        
        // Calculate days from start date to today
        const daysDiff = Math.ceil((now - startDate) / (1000 * 60 * 60 * 24));
        
        // Limit to the selected timeframe
        const maxDays = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : timeframe === '90d' ? 90 : 180;
        const actualDays = Math.min(daysDiff + 1, maxDays); // +1 to include today
        
        // If campaign started recently, show from start date
        // If campaign started long ago, show only the last N days
        let displayStartDate = startDate;
        if (daysDiff > maxDays) {
            // Campaign started more than maxDays ago, show only recent days
            displayStartDate = new Date(now);
            displayStartDate.setDate(displayStartDate.getDate() - (maxDays - 1));
        }
        
        const data = [];
        
        // Generate dates from displayStartDate to today
        const currentDate = new Date(displayStartDate);
        currentDate.setHours(0, 0, 0, 0); // Start of day
        
        const endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999); // End of today
        
        while (currentDate <= endDate) {
            data.push({
                date: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                sent: 0,
                delivered: 0,
                opened: 0,
                clicked: 0,
                bounced: 0,
                unsubscribed: 0
            });
            currentDate.setDate(currentDate.getDate() + 1);
        }
        
        // If campaign has been sent, show data on the send date (or today if sent today)
        if (overview.sent > 0 && data.length > 0) {
            // Find the index for the send date, or use today (last element)
            let dataIndex = data.length - 1; // Default to today
            
            if (campaign.sendAt) {
                const sendDate = new Date(campaign.sendAt);
                sendDate.setHours(0, 0, 0, 0);
                const displayStart = new Date(displayStartDate);
                displayStart.setHours(0, 0, 0, 0);
                
                // Find the index for the send date
                const daysSinceDisplayStart = Math.floor((sendDate - displayStart) / (1000 * 60 * 60 * 24));
                if (daysSinceDisplayStart >= 0 && daysSinceDisplayStart < data.length) {
                    dataIndex = daysSinceDisplayStart;
                }
            }
            
            data[dataIndex] = {
                date: data[dataIndex].date,
                sent: overview.sent || 0,
                delivered: overview.delivered || 0,
                opened: overview.uniqueOpened || 0,
                clicked: overview.uniqueClicked || 0,
                bounced: overview.bounced || 0,
                unsubscribed: overview.unsubscribed || 0
            };
        }
        
        return data;
    };

    const timeSeriesData = generateTimeSeriesData();
    const hasData = overview.sent > 0;

    // Status breakdown data
    const statusBreakdown = [
        { name: 'Delivered', value: overview.delivered || 0, color: '#10b981' },
        { name: 'Opened', value: overview.uniqueOpened || 0, color: '#3b82f6' },
        { name: 'Clicked', value: overview.uniqueClicked || 0, color: '#8b5cf6' },
        { name: 'Bounced', value: overview.bounced || 0, color: '#ef4444' },
        { name: 'Unsubscribed', value: overview.unsubscribed || 0, color: '#f59e0b' }
    ].filter(item => item.value > 0);

    // Rate cards data
    const rateCards = [
        {
            title: 'Open Rate',
            value: `${overview.openRate?.toFixed(1) || 0}%`,
            icon: EyeIcon,
            color: 'blue',
            description: `${overview.uniqueOpened || 0} of ${overview.delivered || 0} delivered`
        },
        {
            title: 'Click Rate',
            value: `${overview.clickRate?.toFixed(1) || 0}%`,
            icon: CursorArrowRaysIcon,
            color: 'purple',
            description: `${overview.uniqueClicked || 0} of ${overview.delivered || 0} delivered`
        },
        {
            title: 'Bounce Rate',
            value: `${overview.bounceRate?.toFixed(1) || 0}%`,
            icon: XCircleIcon,
            color: 'red',
            description: `${overview.bounced || 0} of ${overview.sent || 0} sent`
        },
        {
            title: 'Unsubscribe Rate',
            value: `${overview.unsubscribeRate?.toFixed(1) || 0}%`,
            icon: ExclamationTriangleIcon,
            color: 'orange',
            description: `${overview.unsubscribed || 0} of ${overview.delivered || 0} delivered`
        }
    ];

    const primaryColor = branding?.primaryColor || '#3b82f6';
    const secondaryColor = branding?.secondaryColor || '#10b981';

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button
                        variant="outline"
                        onClick={() => navigate('/campaigns')}
                        className="flex items-center gap-2"
                    >
                        <ArrowLeftIcon className="h-4 w-4" />
                        Back
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">{campaign.name}</h1>
                        <p className="text-sm text-muted-foreground mt-1">Campaign Analytics</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value)}
                        className="px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm"
                    >
                        <option value="7d">Last 7 days</option>
                        <option value="30d">Last 30 days</option>
                        <option value="90d">Last 90 days</option>
                        <option value="all">All time</option>
                    </select>
                    <Link
                        to={`/campaigns/${id}/edit`}
                        className="px-4 py-2 border border-input rounded-md hover:bg-muted text-sm"
                    >
                        Edit Campaign
                    </Link>
                </div>
            </div>

            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Total Sent</p>
                                <p className="text-2xl font-bold text-foreground">{overview.sent || 0}</p>
                            </div>
                            <EnvelopeIcon className="h-8 w-8 text-blue-500" />
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Delivered</p>
                                <p className="text-2xl font-bold text-foreground">{overview.delivered || 0}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {overview.sent > 0 ? `${((overview.delivered / overview.sent) * 100).toFixed(1)}% delivery rate` : '0%'}
                                </p>
                            </div>
                            <CheckCircleIcon className="h-8 w-8 text-green-500" />
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Unique Opens</p>
                                <p className="text-2xl font-bold text-foreground">{overview.uniqueOpened || 0}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {overview.openRate?.toFixed(1) || 0}% open rate
                                </p>
                            </div>
                            <EyeIcon className="h-8 w-8 text-purple-500" />
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Unique Clicks</p>
                                <p className="text-2xl font-bold text-foreground">{overview.uniqueClicked || 0}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {overview.clickRate?.toFixed(1) || 0}% click rate
                                </p>
                            </div>
                            <CursorArrowRaysIcon className="h-8 w-8 text-indigo-500" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Rate Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {rateCards.map((card, index) => {
                    const Icon = card.icon;
                    return (
                        <Card key={index}>
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <p className="text-sm font-medium text-muted-foreground">{card.title}</p>
                                    <Icon className={`h-5 w-5 text-${card.color}-500`} />
                                </div>
                                <p className="text-3xl font-bold text-foreground mb-1">{card.value}</p>
                                <p className="text-xs text-muted-foreground">{card.description}</p>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Engagement Over Time */}
                <Card>
                    <CardHeader>
                        <CardTitle>Engagement Over Time</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {!hasData || timeSeriesData.length === 0 ? (
                            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                                <div className="text-center">
                                    <EyeIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                    <p className="text-sm">No engagement data available yet</p>
                                    <p className="text-xs mt-1">Data will appear once the campaign starts sending</p>
                                </div>
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={timeSeriesData}>
                                <defs>
                                    <linearGradient id="colorOpens" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={primaryColor} stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor={primaryColor} stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={secondaryColor} stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor={secondaryColor} stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="date" stroke="#6b7280" />
                                <YAxis stroke="#6b7280" />
                                <Tooltip 
                                    contentStyle={{ 
                                        backgroundColor: 'var(--popover)', 
                                        border: '1px solid var(--border)',
                                        borderRadius: '8px'
                                    }} 
                                />
                                <Legend />
                                <Area 
                                    type="monotone" 
                                    dataKey="opened" 
                                    stroke={primaryColor} 
                                    fillOpacity={1} 
                                    fill="url(#colorOpens)" 
                                    name="Opens"
                                />
                                <Area 
                                    type="monotone" 
                                    dataKey="clicked" 
                                    stroke={secondaryColor} 
                                    fillOpacity={1} 
                                    fill="url(#colorClicks)" 
                                    name="Clicks"
                                />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>

                {/* Delivery Status */}
                <Card>
                    <CardHeader>
                        <CardTitle>Delivery Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {statusBreakdown.length === 0 ? (
                            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                                <div className="text-center">
                                    <CheckCircleIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                    <p className="text-sm">No delivery data available yet</p>
                                    <p className="text-xs mt-1">Data will appear once emails are sent</p>
                                </div>
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                <Pie
                                    data={statusBreakdown}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {statusBreakdown.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Email Activity Timeline */}
            <Card>
                <CardHeader>
                    <CardTitle>Email Activity Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                    {!hasData || timeSeriesData.length === 0 ? (
                        <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                            <div className="text-center">
                                <ClockIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                <p className="text-sm">No activity data available yet</p>
                                <p className="text-xs mt-1">Timeline will show once the campaign starts sending</p>
                            </div>
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={timeSeriesData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="date" stroke="#6b7280" />
                            <YAxis stroke="#6b7280" />
                            <Tooltip 
                                contentStyle={{ 
                                    backgroundColor: 'var(--popover)', 
                                    border: '1px solid var(--border)',
                                    borderRadius: '8px'
                                }} 
                            />
                            <Legend />
                            <Line 
                                type="monotone" 
                                dataKey="sent" 
                                stroke={primaryColor} 
                                strokeWidth={2}
                                name="Sent"
                            />
                            <Line 
                                type="monotone" 
                                dataKey="delivered" 
                                stroke={secondaryColor} 
                                strokeWidth={2}
                                name="Delivered"
                            />
                            <Line 
                                type="monotone" 
                                dataKey="bounced" 
                                stroke="#ef4444" 
                                strokeWidth={2}
                                name="Bounced"
                            />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </CardContent>
            </Card>

            {/* Step-by-Step Analytics (for sequence/journey campaigns) */}
            {Object.keys(stepAnalytics).length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle>Step-by-Step Performance</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {Object.entries(stepAnalytics).map(([stepId, stepData]) => (
                                <div key={stepId} className="border border-border rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-semibold text-foreground">Step: {stepId}</h4>
                                        <Badge variant="secondary">{stepData.total} recipients</Badge>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                                        <div>
                                            <p className="text-muted-foreground">Sent</p>
                                            <p className="font-semibold text-foreground">{stepData.sent || 0}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">Delivered</p>
                                            <p className="font-semibold text-foreground">{stepData.delivered || 0}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">Opened</p>
                                            <p className="font-semibold text-foreground">{stepData.opened || 0}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">Clicked</p>
                                            <p className="font-semibold text-foreground">{stepData.clicked || 0}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">Exited</p>
                                            <p className="font-semibold text-foreground">{stepData.exited || 0}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Additional Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Replies</p>
                                <p className="text-2xl font-bold text-foreground">{overview.replied || 0}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {overview.replyRate?.toFixed(1) || 0}% reply rate
                                </p>
                            </div>
                            <ArrowPathIcon className="h-8 w-8 text-blue-500" />
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Complaints</p>
                                <p className="text-2xl font-bold text-foreground">{overview.complained || 0}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {overview.complaintRate?.toFixed(1) || 0}% complaint rate
                                </p>
                            </div>
                            <ExclamationTriangleIcon className="h-8 w-8 text-orange-500" />
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Total Recipients</p>
                                <p className="text-2xl font-bold text-foreground">{analytics.recipients || 0}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Campaign status: <StatusBadge type="campaign" status={analytics.status || campaign?.status || 'draft'} />
                                </p>
                            </div>
                            <UserGroupIcon className="h-8 w-8 text-indigo-500" />
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default CampaignAnalytics;

